import clsx from 'clsx';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { FadeIn } from 'components/common/FadeIn';
import DrawCounter from 'components/home-page/DrawCounter';
import { EnterButton } from 'components/home-page/enter-button/EnterButton';
import {
  TRUSTPILOT_REVIEW_COUNT_STRING,
  TRUSTPILOT_SCORE_STRING,
} from 'components/TrustpilotCommon';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { forwardRef, Ref, useEffect, useState } from 'react';

import Trustpilot from '../../svg/Trustpilot/trustpilot-green-hero.svg';
import DesktopWave from '../../svg/Waves/homepage-hero-wave-desktop.svg';
import MobileWave from '../../svg/Waves/homepage-hero-wave-mobile.svg';
import styles from './hero.module.css';

interface HeroProps {
  nextDrawDate: string;
  heroRef?: Ref<HTMLDivElement>;
}

const Hero = observer(({ nextDrawDate, heroRef }: HeroProps) => {
  const { isMobile } = useIsMobile();
  const { subscriptionStatusInfo } = useUserStore();
  const [showEnterButton, setShowEnterButton] = useState(true);

  const [scrollY, _setScrollY] = useState(0);

  // ENG-1836 - disable parallax scrolling for now - in the future, we can put the parallax in a completely separate
  // component so that the state change on scroll does not cause this whole component to rerender. The scroll event
  // can also be changed to a passive scroll event
  // useEffect(() => {
  //   const handleScroll = () => {
  //     setScrollY(window.scrollY);
  //   };

  //   window.addEventListener('scroll', handleScroll);
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, []);

  useEffect(() => {
    if (subscriptionStatusInfo.subscriberStatus) {
      setShowEnterButton(false);
    } else {
      setShowEnterButton(true);
    }
  }, [subscriptionStatusInfo.subscriberStatus]);

  return (
    <>
      <div className={styles.hero} ref={heroRef}>
        <div
          className={styles.heroImage}
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
          }}
        >
          <div className={styles.heroImageGradient} />
        </div>
        <div className={styles.heroContent}>
          <AboutUsSiteContainer styles2026>
            <FadeIn>
              <div className={styles.heroCountdown}>
                <div className="daymadeTypographySBold">Next draw in</div>
                <DrawCounter
                  targetDate={new Date(nextDrawDate)}
                  introText=""
                  edition2026
                />
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className={clsx('daymadeTypographyXXL', styles.heroTitle)}>
                Win prizes that <br />
                make your day
              </div>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div
                className={clsx(
                  'daymadeTypographyMMedium',
                  styles.heroSubtitle,
                )}
              >
                Dream holidays, home renovations {isMobile && <br />}& so much
                more
              </div>
            </FadeIn>
            <div className={styles.heroFooter}>
              {showEnterButton && (
                <FadeIn delay={0.3}>
                  <div className={styles.heroCTA}>
                    <EnterButton
                      dm2026Button
                      buttonFullWidth
                      buttonSize="Large"
                      skipLoadedCheck
                      minWidth={250}
                    />
                  </div>
                </FadeIn>
              )}
              <FadeIn delay={0.3} className={styles.treeAndTrust}>
                <div className={styles.makeATree}>
                  {treePurchaseSvg()}
                  <span>
                    Make a purchase,
                    <br />
                    plant a tree
                  </span>
                </div>
                <div className={styles.treeDivider} />
                <Link
                  className={styles.heroTrustpilot}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.trustpilot.com/review/daymade.co.uk"
                >
                  <Trustpilot />
                  <span className={styles.trustpilotScore}>
                    {TRUSTPILOT_SCORE_STRING}/5 based on{' '}
                    {TRUSTPILOT_REVIEW_COUNT_STRING} reviews
                  </span>
                </Link>
              </FadeIn>
            </div>
          </AboutUsSiteContainer>
        </div>
        <div className={styles.wave}>
          <div className={styles.waveWrapper}>
            {isMobile ? <MobileWave /> : <DesktopWave />}
          </div>
        </div>
      </div>
    </>
  );
});

const treePurchaseSvg = () => (
  <svg
    width="38"
    height="38"
    viewBox="0 0 38 38"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.999 37.4743C8.81237 37.4742 0.524968 29.187 0.525025 19.0001C0.525082 8.81346 8.81257 0.52606 18.9992 0.526117C29.1859 0.526174 37.4733 8.81366 37.4732 19.0003C37.4732 29.1872 29.1857 37.4744 18.999 37.4743M18.9999 0.000106301C8.52327 4.76863e-05 -4.76878e-05 8.52353 -0.000106301 18.9999C-0.000164916 29.4765 8.52305 37.9998 18.9997 37.9999C29.4763 37.9999 37.9996 29.4767 37.9997 19.0001C37.9997 8.52374 29.4765 0.000164916 18.9999 0.000106301"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M20.0345 28.6322C19.9887 27.5788 19.9196 26.525 19.9194 25.4716C19.9188 24.2368 19.8305 22.9988 20.0632 21.7678C20.1699 21.2039 20.3795 20.6994 20.6976 20.2435C21.1077 19.6557 21.6569 19.229 22.3334 18.9624C22.6204 18.8489 22.9091 18.8163 23.2086 18.7939C23.3576 18.7829 23.5046 18.6964 23.6505 18.7014C23.8979 18.7095 24.145 18.7555 24.3895 18.7989C24.9411 18.8973 25.408 19.1978 25.8988 19.4407C25.9325 19.4575 25.9716 19.4622 26.0084 19.4727C25.3688 18.9146 24.6372 18.5647 23.809 18.4114C22.9633 18.255 22.1759 18.5218 21.3711 18.7308C21.4266 18.6073 21.4868 18.4995 21.5623 18.404C21.8291 18.0662 22.0875 17.7197 22.3749 17.3998C22.7887 16.9386 23.2491 16.5282 23.8464 16.3132C23.9886 16.2619 24.1413 16.2406 24.2886 16.2036C24.5268 16.1436 24.7642 16.0337 25.0029 16.0314C25.4716 16.0266 25.9412 16.0813 26.4104 16.1116C26.4817 16.1163 26.5529 16.121 26.6244 16.126C26.1213 15.9046 25.5986 15.7816 25.0602 15.8097C24.5328 15.8368 24.0086 15.9196 23.4539 15.9809C23.7133 15.57 23.8406 15.1272 23.8527 14.6569C23.8648 14.1966 23.8027 13.7475 23.5832 13.3332C23.5738 13.4791 23.5914 13.6203 23.6192 13.7599C23.7693 14.5126 23.6776 15.214 23.1318 15.7916C22.7729 16.1712 22.3902 16.5298 22.0008 16.8787C21.2323 17.5669 20.5275 18.3104 19.9331 19.1562C19.8523 19.2711 19.7601 19.3781 19.6688 19.4937C19.6475 19.4632 19.6352 19.454 19.6349 19.4441C19.605 18.5481 19.5676 17.6519 19.5513 16.7556C19.5458 16.4499 19.5661 16.1381 19.6239 15.8384C19.6938 15.474 19.7856 15.1078 19.9215 14.7636C20.2136 14.0249 20.7194 13.4983 21.5197 13.3051C21.5831 13.2898 21.6464 13.2746 21.709 13.2562C21.7697 13.238 21.8291 13.2162 21.8891 13.196C20.5919 13.1812 19.8703 13.9549 19.3747 15.0652C19.3529 14.9742 19.3416 14.9169 19.3255 14.8612C19.146 14.2344 19.0127 13.5898 18.7747 12.9861C18.3907 12.0116 17.7232 11.2907 16.6768 10.9918C16.6397 10.9812 16.5993 10.9839 16.5603 10.9802C16.544 10.9962 16.5275 11.012 16.5109 11.0275C18.0439 11.6743 18.418 12.9896 18.5739 14.5033C18.1277 14.0837 17.7018 13.7267 17.171 13.5374C16.6329 13.3455 15.7764 13.3045 15.5079 13.4533C15.6486 13.468 15.7643 13.4822 15.8802 13.4925C16.3574 13.5353 16.8272 13.595 17.2305 13.8931C17.643 14.1984 18.0015 14.5543 18.312 14.9629C18.6357 15.388 18.7613 15.8641 18.7605 16.3936C18.7595 17.1266 18.7789 17.8601 18.8042 18.5925C18.8289 19.2995 18.8754 20.0056 18.9056 20.712C18.9164 20.9699 18.9072 21.2286 18.9072 21.5465C18.8128 21.4476 18.7505 21.394 18.7027 21.3296C18.5696 21.1511 18.4513 20.961 18.3112 20.7883C17.7199 20.0579 17.0509 19.4072 16.3029 18.8359C15.4151 18.1587 15.0891 17.2782 15.378 16.1859C15.4141 16.0505 15.4398 15.912 15.4706 15.775C14.9214 16.6057 14.9892 17.472 15.3375 18.3349C14.7897 18.2654 14.2652 18.164 13.7375 18.1424C13.2036 18.1206 12.6849 18.2631 12.1875 18.4668C12.4546 18.4589 12.7185 18.4329 12.9812 18.3966C13.4915 18.3267 13.9989 18.3412 14.4989 18.4621C14.6169 18.491 14.72 18.5775 14.8328 18.6311C15.0594 18.7389 15.2923 18.8349 15.5142 18.9519C15.612 19.0034 15.6877 19.0957 15.7761 19.1667C15.9314 19.2921 16.0929 19.4104 16.2435 19.5413C16.3255 19.6126 16.3928 19.7017 16.4625 19.7866C16.7312 20.1142 17.023 20.4265 17.2591 20.7762C17.5288 21.1763 17.7486 21.6101 17.9894 22.0297C18.0575 22.148 18.1238 22.2676 18.1911 22.3867C17.3726 22.1622 16.5774 22.0305 15.7574 22.1543C15.0102 22.2676 13.9263 22.791 13.5635 23.2238C13.6539 23.1767 13.7099 23.1494 13.7646 23.1194C14.2586 22.8483 14.7602 22.5928 15.3162 22.4758C15.4984 22.4377 15.6903 22.434 15.8775 22.4348C16.0056 22.4353 16.1339 22.489 16.2611 22.4855C16.4404 22.4808 16.6113 22.3927 16.7814 22.567C16.85 22.637 17.0346 22.5778 17.1518 22.6188C17.5548 22.7605 17.8648 23.0348 18.0796 23.3947C18.4082 23.9455 18.5604 24.5617 18.5546 25.1948C18.5499 25.7337 18.4963 26.28 18.3914 26.8087C18.1963 27.7933 17.8206 28.7087 17.177 29.4971C17.0805 29.6157 16.9953 29.7437 16.9049 29.867C16.9128 29.8907 16.9209 29.9144 16.9288 29.9378C17.2191 29.9378 17.5101 29.9533 17.7988 29.932C17.9767 29.9191 18.1489 29.8352 18.3275 29.81C18.5026 29.7853 18.6708 29.7695 18.8562 29.8573C19.0115 29.9309 19.2192 29.8949 19.404 29.9073C19.6249 29.922 19.8457 29.9436 20.0671 29.9499C20.3437 29.9575 20.3915 29.8912 20.2671 29.6373C20.1107 29.3179 20.0497 28.9795 20.0345 28.6322"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M14.7921 21.6571C14.9425 21.6182 15.1223 21.7102 15.2895 21.7147C15.4625 21.7189 15.6389 21.7013 15.8096 21.6697C15.9925 21.6361 16.1845 21.5081 16.348 21.5415C16.6256 21.598 16.7634 21.426 16.9611 21.3043C16.6122 20.8542 16.2121 20.472 15.7599 20.146C15.462 19.9314 15.1534 19.7185 14.7698 19.7085C14.4632 19.7006 14.1507 19.7011 13.8502 19.7529C13.4621 19.8197 13.0838 19.9414 12.701 20.0392C12.6929 20.0666 12.6842 20.0939 12.6758 20.121C12.9755 20.4267 13.2668 20.7414 13.5781 21.0348C13.756 21.2026 13.9514 21.3658 14.1669 21.4757C14.3541 21.5714 14.5305 21.7255 14.7921 21.6571"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M25.4244 16.529C24.5536 16.4919 23.8112 16.8821 23.0664 17.3269C23.3201 17.6253 23.6098 17.8183 23.9303 17.95C24.3551 18.1248 24.7363 18.424 25.2277 18.4216C25.3479 18.4208 25.4712 18.4776 25.5879 18.4642C25.7525 18.4456 25.921 18.4061 26.0724 18.3399C26.3726 18.2087 26.6668 18.0612 26.955 17.9045C27.1451 17.8015 27.3204 17.6705 27.5023 17.552C27.5026 17.5252 27.5029 17.4989 27.5031 17.4723C27.0964 17.2402 26.7 16.9865 26.2791 16.7838C26.0133 16.6555 25.7136 16.5411 25.4244 16.529"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M22.4157 19.2983C21.8878 19.4489 21.52 19.8283 21.1777 20.2266C20.9369 20.5063 20.7274 20.8126 20.5039 21.1067C20.5139 21.1312 20.5239 21.1554 20.5336 21.1798C20.7176 21.2258 20.8993 21.2931 21.086 21.3121C21.2718 21.331 21.4719 21.2608 21.6488 21.3023C21.9625 21.3757 22.2451 21.3073 22.5453 21.2393C22.9055 21.1575 23.2347 21.0442 23.4957 20.7729C23.6132 20.6511 23.7481 20.5441 23.8554 20.414C24.1288 20.0822 24.4122 19.7549 24.561 19.3048C24.3247 19.272 24.1017 19.2291 23.8767 19.212C23.3866 19.1739 22.8942 19.1613 22.4157 19.2983"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M12.2157 17.0601C12.3582 17.2318 12.5351 17.4311 12.7336 17.4892C13.1364 17.6062 13.5118 17.8344 13.9482 17.8296C13.9677 17.8296 13.99 17.831 14.0058 17.8404C14.2587 17.9947 14.5166 18.0371 14.7966 17.8628C14.7729 17.4642 14.0715 16.0724 13.5899 15.7532C12.8935 15.2918 12.1063 15.2308 11.2969 15.1846C11.3003 15.2416 11.2966 15.2687 11.304 15.2916C11.5085 15.9325 11.7838 16.5398 12.2157 17.0601"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M22.6755 22.2764C21.9799 21.789 21.1523 21.7611 20.3047 21.7303C20.3315 21.8245 20.3394 21.8899 20.3678 21.9449C20.5989 22.3908 20.7987 22.8585 21.0792 23.271C21.2832 23.5712 21.509 23.8906 21.9068 24.0037C22.1408 24.0702 22.3561 24.214 22.5917 24.2592C22.8262 24.3042 23.0759 24.2689 23.3191 24.2689C23.3194 24.2816 23.3196 24.2944 23.3199 24.3071C23.2463 24.3115 23.1729 24.3163 23.0993 24.321C23.1009 24.336 23.1025 24.3512 23.1038 24.3662C23.3859 24.3381 23.6677 24.31 23.9898 24.2779C23.6509 23.5107 23.3549 22.7525 22.6755 22.2764"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M13.9751 23.6009C13.5082 24.1454 13.2421 24.801 13.0547 25.5285C13.3273 25.5285 13.5718 25.5403 13.8147 25.5238C13.9567 25.514 14.0955 25.4591 14.2362 25.4247C14.2425 25.4231 14.249 25.422 14.2553 25.421C14.6565 25.3497 14.976 25.1002 15.3301 24.927C15.3411 24.9215 15.3474 24.9075 15.3569 24.8986C15.9271 24.3536 16.2587 23.6706 16.4913 22.9313C16.5095 22.8735 16.5111 22.8109 16.5253 22.7181C16.1901 22.767 15.8827 22.7891 15.587 22.8603C15.003 23.0012 14.3894 23.118 13.9751 23.6009"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M24.3854 14.5958C24.2937 14.8907 24.2164 15.1904 24.1328 15.4878C24.1515 15.5093 24.1704 15.5312 24.1893 15.5527C24.5997 15.4494 25.0185 15.3705 25.4176 15.2343C25.6957 15.1389 25.95 14.9699 26.2089 14.8229C26.2981 14.7724 26.3788 14.698 26.4461 14.6197C26.994 13.9837 27.1801 13.2092 27.3016 12.3898C27.2358 12.3898 27.1814 12.3771 27.1362 12.3916C26.6467 12.5504 26.148 12.6884 25.6739 12.8856C25.4284 12.9879 25.197 13.1685 25.013 13.3641C24.6896 13.7079 24.5243 14.1501 24.3854 14.5958"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M16.2503 16.0527C16.1297 15.9875 15.9895 15.9583 15.8321 15.9034C15.8307 16.5664 15.8828 17.1806 16.076 17.776C16.2608 18.3452 16.6859 18.7064 17.1665 19.0167C17.3201 19.1158 17.4973 19.1878 17.6282 19.3106C17.8338 19.5033 18.0596 19.5782 18.3409 19.5491C18.3488 19.5044 18.3609 19.4673 18.3611 19.4302C18.368 18.8978 18.296 18.3747 18.159 17.8617C18.0331 17.3896 17.8256 16.9624 17.3871 16.6992C17.0135 16.4747 16.6336 16.2604 16.2503 16.0527"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M20.8229 14.5295C20.4459 14.886 20.1546 15.3058 20.1034 15.8419C20.045 16.4502 20.0697 17.0562 20.2193 17.6525C20.2311 17.699 20.2595 17.7413 20.2866 17.7992C20.3589 17.7592 20.4207 17.735 20.4706 17.6961C20.7138 17.505 20.9675 17.3244 21.192 17.113C21.573 16.7547 21.9373 16.3753 22.0499 15.8366C22.2018 15.1087 22.0914 14.398 21.8971 13.6682C21.8356 13.694 21.8017 13.7003 21.7786 13.7195C21.4581 13.9871 21.1258 14.2429 20.8229 14.5295"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M19.4891 13.6781C19.9573 13.3753 20.3997 13.0327 20.7497 12.6015C20.9497 12.3552 21.0993 12.0471 21.1963 11.7427C21.3362 11.3026 21.302 10.8372 21.2342 10.3824C21.1977 10.1347 21.1393 9.89077 21.0857 9.61946C20.5596 9.94361 20.1058 10.2959 19.7407 10.7449C19.5742 10.9495 19.4102 11.1811 19.3321 11.4277C19.1058 12.1433 19.1825 12.8742 19.309 13.6C19.3292 13.7165 19.3913 13.7412 19.4891 13.6781"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M23.675 21.2022C23.3046 21.3134 22.9362 21.4309 22.5469 21.5518C23.4597 22.717 24.8428 23.0888 26.0556 22.1211C25.8818 21.9778 25.7159 21.8334 25.5421 21.6994C24.99 21.2724 24.398 20.9853 23.675 21.2022"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M27.5176 14.612C27.1062 14.7235 26.7137 14.9078 26.3169 15.0695C26.2131 15.1115 26.1234 15.188 26.0117 15.2585C26.1437 15.3739 26.2362 15.5046 26.3614 15.5556C26.7644 15.7201 27.1017 16.0106 27.5607 16.084C27.8239 16.1258 28.0358 16.1226 28.2961 16.0656C28.718 15.9733 29.0206 15.7025 29.3745 15.509C29.435 15.4759 29.4833 15.4202 29.5767 15.3418C29.2441 15.1541 28.9446 14.9711 28.6328 14.8118C28.284 14.633 27.902 14.5079 27.5176 14.612"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M25.9023 18.8064C26.1468 18.9568 26.3677 19.1001 26.5956 19.2307C26.7831 19.3383 26.9695 19.4789 27.1732 19.5202C27.4908 19.5843 27.8178 19.8007 28.1507 19.5502C28.1751 19.5318 28.2238 19.5478 28.2603 19.5433C28.3302 19.5347 28.4109 19.5412 28.4675 19.5081C28.7656 19.3343 29.0585 19.1511 29.3511 18.9676C29.3858 18.9458 29.4079 18.9045 29.4604 18.8443C29.2068 18.6942 28.9751 18.5496 28.7364 18.4176C27.8336 17.917 26.7307 18.055 25.9023 18.8064"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M16.5582 13.1734C16.8687 13.1456 17.165 13.1188 17.4894 13.0898C17.455 12.9957 17.44 12.925 17.4053 12.8658C17.2292 12.5677 17.0704 12.2554 16.8608 11.9825C16.6968 11.7688 16.4957 11.549 16.2612 11.4309C15.7101 11.1536 15.1041 11.1507 14.4727 11.1988C14.6388 11.6833 14.8862 12.0813 15.199 12.4378C15.4835 12.762 15.8253 13.0012 16.2625 13.067C16.3863 13.0856 16.5154 13.0696 16.6421 13.0696C16.6192 13.0975 16.5953 13.1032 16.5719 13.1111C16.5693 13.1122 16.5693 13.1216 16.5582 13.1734"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M12.3773 21.2353C11.9033 21.3473 11.4668 21.54 11.0352 21.8113C11.1792 21.9383 11.2946 22.0655 11.434 22.1562C11.6348 22.2874 11.8562 22.3865 12.0631 22.5093C12.4091 22.7141 12.7947 22.7559 13.1765 22.7364C13.3921 22.7257 13.6071 22.6124 13.8132 22.524C14.0696 22.4139 14.3162 22.2822 14.5983 22.1447C14.2078 21.7721 13.7969 21.5495 13.3687 21.3531C13.0506 21.2071 12.7243 21.1533 12.3773 21.2353"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M10.3398 18.3469C10.8633 18.5439 11.3373 18.2878 11.8292 18.2255C12.0708 18.195 12.3074 18.0507 12.4696 17.8014C12.2929 17.6721 12.1271 17.5196 11.9346 17.4157C11.5921 17.2309 11.249 16.9993 10.8785 16.9249C10.1811 16.7842 9.57718 17.1281 8.96093 17.4504C9.25486 17.8266 9.77251 18.1335 10.3398 18.3469"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M12.9221 18.7328C12.5356 18.8017 12.1665 18.8827 11.8084 18.9986C11.3715 19.14 10.9853 19.3551 10.7602 19.7944C10.5536 20.1979 10.39 20.6125 10.2851 21.1073C10.9923 21.0164 11.6817 20.9906 12.1759 20.4482C12.6103 19.9721 12.8179 19.3816 12.9221 18.7328"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M24.8477 19.4554C24.9481 20.1392 25.0848 20.783 25.5262 21.2783C25.9802 21.7876 26.6159 21.9593 27.3397 22.0239C27.1975 21.3262 27.0679 20.67 26.5733 20.2015C26.1104 19.7627 25.502 19.5847 24.8477 19.4554"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M17.2986 10.8338C17.3041 10.741 17.3191 10.6868 17.3083 10.6385C17.2174 10.2252 17.1427 9.80638 17.0199 9.40257C16.939 9.13651 16.7933 8.88886 16.5533 8.71586C16.1187 8.40222 15.6366 8.19742 15.0742 8.04993C15.1505 8.77948 15.2117 9.48563 15.7117 10.0322C16.1287 10.4881 16.6968 10.6813 17.2986 10.8338"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M23.9878 13.319C24.034 13.467 24.1205 13.6029 24.2083 13.7846C24.3932 13.5617 24.5672 13.3971 24.685 13.1991C24.884 12.8647 25.1282 12.5269 25.2079 12.1594C25.3551 11.4776 25.0699 10.8585 24.7318 10.2318C24.3895 10.5859 24.1261 10.9474 23.9197 11.3512C23.7449 11.6927 23.6612 12.0508 23.7517 12.4317C23.8221 12.7296 23.8968 13.0269 23.9878 13.319"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M13.8823 12.8857C13.8331 13.0308 13.7827 13.1375 13.7616 13.2498C13.6893 13.6331 13.5992 14.0162 13.5673 14.4034C13.51 15.1012 13.9712 15.5628 14.3808 16.0376C14.4804 16.1533 14.6108 16.2422 14.7635 16.3747C14.981 15.7048 15.2031 15.0699 15.0449 14.4068C14.8897 13.7569 14.4 13.3245 13.8823 12.8857"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M18.7619 11.872C19.4189 10.1224 19.1021 9.39703 17.8768 8.43665C17.6117 9.05946 17.3704 10.1161 17.7884 10.7688C18.0539 11.1832 18.3405 11.5757 18.7619 11.872"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M26.918 19.8333C27.0468 20.1062 27.1522 20.358 27.281 20.597C27.5197 21.0387 27.8768 21.356 28.3552 21.5158C28.6875 21.6268 29.0235 21.5256 29.3569 21.4867C29.3661 21.4856 29.3729 21.4683 29.3808 21.4585C28.9229 20.194 28.4023 19.8001 26.918 19.8333"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M10.3828 24.054C10.8752 24.4639 11.4399 24.2302 11.9676 24.2725C12.4634 24.0114 12.8622 23.6484 13.1698 23.172C12.0788 22.6525 10.9759 23.0084 10.3828 24.054"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M11.2197 18.8319C9.85709 18.4039 9.24479 18.5346 8.41796 19.7337C8.56913 19.8025 8.70768 19.8685 8.84833 19.9298C9.18353 20.0754 9.52241 19.9471 9.8584 19.9324C9.99274 19.9261 10.1476 19.8867 10.2525 19.8081C10.5117 19.6133 10.7541 19.3943 10.9891 19.1698C11.0838 19.0791 11.1414 18.9494 11.2197 18.8319"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M14.4009 11.0261C14.5541 11.0174 14.7037 10.948 14.8554 10.908C15.1843 10.8205 15.5153 10.7382 15.7822 10.47C15.4136 10.1283 15.0032 9.85958 14.521 9.71498C14.3935 9.67686 14.2381 9.6482 14.1175 9.68553C13.7336 9.80515 13.3595 9.95684 12.9828 10.0999C12.9402 10.1159 12.9071 10.1577 12.8477 10.2053C13.0824 10.382 13.2796 10.5736 13.5136 10.6945C13.7928 10.8391 14.0636 11.045 14.4009 11.0261"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M23.303 10.8685C22.0773 10.9818 21.2394 11.8313 21.2581 12.9297C22.3368 12.9859 23.2606 12.2814 23.303 10.8685"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
    <path
      d="M15.3672 14.0143C15.4114 14.1144 15.4408 14.2254 15.5026 14.3129C15.8123 14.7525 16.0723 15.2347 16.6909 15.3411C17.1241 15.4155 17.5432 15.5199 17.9604 15.298C17.6989 14.279 16.3654 13.6062 15.3672 14.0143"
      fill-rule="evenodd"
      clip-rule="evenodd"
      fill="white"
    />
  </svg>
);

/** Allows consumers of the component to retrieve the rendered outer HTML element */
export const HeroWithForwardedRef = forwardRef<HTMLDivElement, HeroProps>(
  (props, ref) => <Hero {...props} heroRef={ref} />,
);

export default Hero;
